/**
 * Status Color Utility
 * 
 * Provides consistent status colors across the application.
 * All colors meet WCAG AA accessibility standards (4.5:1 contrast minimum).
 * 
 * @see STATUS_COLOR_CONSISTENCY_ANALYSIS.md for full documentation
 */

export type StatusType = 
  | 'success' 
  | 'progress' 
  | 'pending' 
  | 'warning' 
  | 'error';

export interface StatusColorClasses {
  /** Full Tailwind classes for badge/pill styling */
  badge: string;
  /** Text color only */
  text: string;
  /** Background color only */
  background: string;
}

/**
 * Maps common status values to their semantic type
 */
export const STATUS_MAPPING: Record<string, StatusType> = {
  // Success states
  'completed': 'success',
  'active': 'success',
  'approved': 'success',
  'on track': 'success',
  'success': 'success',
  'current': 'success',
  
  // Progress states
  'in progress': 'progress',
  'working': 'progress',
  'draft': 'progress',
  
  // Pending states
  'not started': 'pending',
  'pending': 'pending',
  'inactive': 'pending',
  'waiting': 'pending',
  
  // Warning states
  'on hold': 'warning',
  'at risk': 'warning',
  'warning': 'warning',
  'rejected': 'warning',
  
  // Error states
  'cancelled': 'error',
  'closed': 'error',
  'over budget': 'error',
  'critical': 'error',
  'error': 'error',
};

/**
 * Core status color definitions
 * All colors meet WCAG AA standards
 */
export const STATUS_COLORS: Record<StatusType, StatusColorClasses> = {
  success: {
    badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    text: 'text-green-800 dark:text-green-300',
    background: 'bg-green-100 dark:bg-green-900',
  },
  progress: {
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    text: 'text-blue-800 dark:text-blue-300',
    background: 'bg-blue-100 dark:bg-blue-900',
  },
  pending: {
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
    text: 'text-gray-800 dark:text-gray-300',
    background: 'bg-gray-100 dark:bg-gray-900',
  },
  warning: {
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
    text: 'text-amber-800 dark:text-amber-300',
    background: 'bg-amber-100 dark:bg-amber-900',
  },
  error: {
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    text: 'text-red-800 dark:text-red-300',
    background: 'bg-red-100 dark:bg-red-900',
  },
};

/**
 * Get status color classes for a given status value
 * 
 * @param status - The status string (case-insensitive)
 * @param type - Return type: 'badge' (default), 'text', or 'background'
 * @returns Tailwind CSS classes for the status
 * 
 * @example
 * ```tsx
 * <Badge className={getStatusColor('completed')}>Completed</Badge>
 * <Badge className={getStatusColor('in progress')}>In Progress</Badge>
 * <span className={getStatusColor('on hold', 'text')}>On Hold</span>
 * ```
 */
export function getStatusColor(
  status: string | undefined | null,
  type: 'badge' | 'text' | 'background' = 'badge'
): string {
  if (!status) {
    return STATUS_COLORS.pending[type];
  }

  const normalizedStatus = status.toLowerCase().trim();
  const statusType = STATUS_MAPPING[normalizedStatus] || 'pending';
  
  return STATUS_COLORS[statusType][type];
}

/**
 * Get the semantic status type for a given status value
 * 
 * @param status - The status string (case-insensitive)
 * @returns The semantic status type
 * 
 * @example
 * ```tsx
 * getStatusType('completed') // 'success'
 * getStatusType('in progress') // 'progress'
 * ```
 */
export function getStatusType(status: string | undefined | null): StatusType {
  if (!status) {
    return 'pending';
  }

  const normalizedStatus = status.toLowerCase().trim();
  return STATUS_MAPPING[normalizedStatus] || 'pending';
}

/**
 * Check if a status represents a positive/successful state
 */
export function isSuccessStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'success';
}

/**
 * Check if a status represents an active/in-progress state
 */
export function isProgressStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'progress';
}

/**
 * Check if a status represents a warning/at-risk state
 */
export function isWarningStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'warning';
}

/**
 * Check if a status represents an error/critical state
 */
export function isErrorStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'error';
}

/**
 * Legacy color mapping for backward compatibility
 * @deprecated Use getStatusColor() instead
 */
export const LEGACY_STATUS_COLORS: Record<string, string> = {
  'Active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  'Completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  'In Progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  'Not Started': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  'On Hold': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  'Pending': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  'Inactive': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  'Closed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};
